import React, { useState } from 'react';

import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AttachmentsModal from 'bundles/apex/tasks/attachment_modal';
import { refreshActivities } from 'bundles/apex/activities/hooks';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { UndoControl } from 'components/ui/snackbar/undo_control';
import { deleteRequest } from 'helpers/api';
import { requestErrors } from 'helpers/api_errors';

import type { Attachment, Task } from 'bundles/apex/tasks/types';

export type CheckboxState = 'idle' | 'anim' | 'settle' | 'checked';

interface Props {
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  attachmentsPath: string;
  deleteTaskAttachment: (task: Task, attachment: Attachment) => void;
  features?: {
    taskAttachmentExceptions?: boolean;
  };
  restoreTask?: (task: Task) => void;
  state?: CheckboxState;
  task: Task;
  onUpdate: (task: Task) => Promise<Task>;
}

export default function Status({
  addTaskAttachment,
  attachmentsPath,
  deleteTaskAttachment,
  features,
  restoreTask,
  state = 'idle',
  task,
  onUpdate,
}: Props) {
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  // Rethrows, so AttachmentsModal stays open with the reason the user typed.
  async function onModalSave(task) {
    try {
      await onUpdate({
        ...task,
        completed: task.attachments.length > 0 || !!task.attachmentExceptionReason,
      });
    } catch (errors) {
      showUpdateFailure(errors);
      throw errors;
    }
  }

  function onModalHide() {
    // onModalSave has said why already.
    onModalSave(task).catch(() => {});
    setShowAttachmentsModal(false);
  }

  function updateFailureText(errors: unknown) {
    return requestErrors(errors, 'The task could not be updated. Please try again.').join(' ');
  }

  function showUpdateFailure(errors: unknown) {
    showSnackbar({ text: updateFailureText(errors), variant: 'danger' });
  }

  async function toggleComplete() {
    if (
      !task.completed && task.requiresAttachment && task.attachments.length === 0 &&
      (!features?.taskAttachmentExceptions || !task.attachmentExceptionReason)
    ) {
      setShowAttachmentsModal(true);
    } else {
      const nextCompleted = !task.completed;
      const previousCompleted = task.completed;
      let savedTask: Task;
      try {
        savedTask = await onUpdate({ ...task, completed: nextCompleted });
      } catch (errors) {
        showUpdateFailure(errors);
        return;
      }
      // Completing a task with a type creates an activity; undo should remove it too.
      const createdActivityId = savedTask.createdActivityId;
      // A failed undo replaces the snackbar, so the failure offers Undo again.
      const undo = async () => {
        try {
          const restored = await onUpdate({ ...task, completed: previousCompleted });
          restoreTask?.(restored);
        } catch (errors) {
          showSnackbar({
            text: (
              <div className="d-flex flex-column">
                {updateFailureText(errors)}
                <UndoControl onUndo={undo} />
              </div>
            ),
            variant: 'danger',
          });
          return;
        }
        if (!createdActivityId) return;

        try {
          await deleteRequest(`/apex/activities/${createdActivityId}`);
          refreshActivities();
        } catch {
          showSnackbar({
            text: 'The task was restored, but its activity could not be removed.',
            variant: 'danger',
          });
        }
      };
      showSnackbar({
        text: (
          <div className="d-flex flex-column">
            {nextCompleted ? 'Task completed' : 'Task marked incomplete'}
            <UndoControl onUndo={undo} />
          </div>
        ),
        variant: 'success',
      });
    }
  }

  return <>
    <button
      aria-checked={!!task.completed}
      aria-label={task.completed ? 'Mark task incomplete' : 'Mark task complete'}
      className="task-checkbox"
      data-state={state}
      role="checkbox"
      type="button"
      onClick={toggleComplete}
    >
      <FontAwesomeIcon aria-hidden="true" icon={faCheck} />
    </button>

    <AttachmentsModal
      addTaskAttachment={attachment => addTaskAttachment(task, attachment)}
      attachmentExceptions={features?.taskAttachmentExceptions}
      attachmentsPath={attachmentsPath}
      deleteTaskAttachment={attachment => deleteTaskAttachment(task, attachment)}
      show={showAttachmentsModal}
      task={task}
      onHide={onModalHide}
      onSave={onModalSave}
    />
  </>;
}
